import { Divider, Stack } from '@mui/material';

import Card from '@/components/card';
import { useAppSelector } from '@/hooks/redux';
import { Labels } from '@/models/labels';

import { usePostLabels } from '../hooks/usePostLabels';
import { PendingLabel } from '../types';
import { isSameName, parseCategoryLabel } from '../utils/matching';
import { buildLabelText, buildPendingLabelText } from '../utils/text';
import { LabelAutocomplete } from './LabelAutocomplete';
import { LabelChip } from './LabelChip';
import { LabelsFooter } from './LabelsFooter';
import { RemoveLabelPopover } from './RemoveLabelPopover';
import { StagedChip } from './StagedChips';

type Props = { postId: number; labels: Labels.Details[] };

export const LabelsCard = ({ postId, labels }: Props) => {
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const post = usePostLabels(postId, labels);

  const trimmedInput = post.inputValue.trim();
  const isLabelMatching = (
    label: Pick<Labels.Details, 'name' | 'category'> | PendingLabel
  ): boolean => {
    if (!post.isPickerOpen || !trimmedInput) return false;

    const parsed = parseCategoryLabel(trimmedInput);
    if (parsed) {
      if (!isSameName(label.name, parsed.labelName)) return false;

      const categoryName =
        'category' in label ? label.category?.name : label.categoryName;

      return !!categoryName && isSameName(categoryName, parsed.categoryName);
    }

    return isSameName(label.name, trimmedInput);
  };

  const compareLabels = (a: Labels.Details, b: Labels.Details) => {
    if (!a.category !== !b.category) return a.category ? -1 : 1;

    return (a.category?.name ?? '').localeCompare(b.category?.name ?? '');
  };

  return (
    <Card
      sx={{ marginBottom: 2 }}
      isCollapsible
      title="Labels"
      content={
        <Stack gap={1.5}>
          {(labels.length > 0 || post.pending.length > 0) && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {labels.sort(compareLabels).map((label) => {
                const isCurrentUser = currentUser?.id === label.assignedBy?.id;
                return post.removing.includes(label.id) ? (
                  <StagedChip
                    key={label.id}
                    color="error"
                    status="REMOVING"
                    label={
                      <span
                        style={{
                          textDecoration: 'line-through',
                          color: 'inherit'
                        }}
                      >
                        {buildLabelText(label)}
                      </span>
                    }
                    onUndo={() => post.undoRemoval(label.id)}
                  />
                ) : (
                  <LabelChip
                    key={label.id}
                    label={label}
                    isCurrentUser={isCurrentUser}
                    isPendingConfirmation={post.isPendingConfirmation(label.id)}
                    isHighlighted={isLabelMatching(label)}
                    onDelete={(e) =>
                      post.requestRemoval(
                        label,
                        isCurrentUser,
                        (e.currentTarget as HTMLElement).closest(
                          '.MuiChip-root'
                        ) ?? (e.currentTarget as HTMLElement)
                      )
                    }
                  />
                );
              })}
              {post.pending.map((item) => (
                <StagedChip
                  key={item.name}
                  color="primary"
                  status="ADDING"
                  isHighlighted={isLabelMatching(item)}
                  label={buildPendingLabelText(item)}
                  onDelete={() => post.removePending(item.name)}
                />
              ))}
            </Stack>
          )}

          <LabelAutocomplete
            isPickerOpen={post.isPickerOpen}
            onOpenChange={post.setIsPickerOpen}
            inputValue={post.inputValue}
            onInputValueChange={post.setInputValue}
            groupedOptions={post.groupedOptions}
            categoryBadges={post.categoryBadges}
            catalogLabels={post.catalogLabels}
            isTaken={post.isTaken}
            isAssigned={post.isAssigned}
            isPending={post.isPending}
            isUnavailable={post.isUnavailable}
            lookups={{
              findCategoryName: post.findCategoryName,
              findLabelName: post.findLabelName,
              isLabelLinkedCategory: post.isLabelLinkedCategory
            }}
            onAdd={post.addPending}
          />

          <Divider />
          <LabelsFooter
            pendingText={post.pendingText}
            isActionDisabled={post.isActionDisabled}
            isSaving={post.isSaving}
            onDiscard={post.discard}
            onSave={post.save}
          />
          <RemoveLabelPopover
            label={post.labelToConfirm}
            anchorEl={post.confirmAnchor}
            onClose={post.closeConfirm}
            onConfirm={post.confirmRemoval}
          />
        </Stack>
      }
    />
  );
};
